import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useProfile } from "@/context/ProfileContext";
import { UserAvatar } from "@/components/ui-nw/UserAvatar";
import { errorMessage } from "@/lib/api";
import { timeAgo } from "@/lib/format";

export const Comments = ({ trackId, kind = "tracks", onCountChange }) => {
  const { request } = useProfile();
  const [comments, setComments] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { request("get", `/${kind}/${trackId}/comments`).then(setComments).catch(() => setComments([])); }, [trackId, kind, request]);

  const post = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const c = await request("post", `/${kind}/${trackId}/comments`, { text });
      setComments((cs) => [c, ...cs]); setText(""); onCountChange?.(1);
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await request("delete", kind === "videos" ? `/video-comments/${id}` : `/comments/${id}`); setComments((cs) => cs.filter((c) => c.id !== id)); onCountChange?.(-1); }
    catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <section data-testid="comments-section" className="space-y-4">
      <h2 className="font-display text-xl font-bold text-white">Comments</h2>
      <form onSubmit={post} className="flex gap-2">
        <input data-testid="comment-input" maxLength={500} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment..." className="nw-input" />
        <button data-testid="comment-submit-button" disabled={busy || !text.trim()} aria-label="Post comment" className="btn-red grid h-12 w-12 shrink-0 place-items-center disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
      {comments === null ? <Loader2 className="h-5 w-5 animate-spin text-nw-red" /> : comments.length === 0 ? (
        <p className="text-sm text-slate-500">No comments yet. Start the conversation.</p>
      ) : (
        <ul className="space-y-3">
          {comments.map((c) => (
            <li key={c.id} data-testid={`comment-${c.id}`} className="flex gap-3 rounded-2xl border border-white/10 bg-nw-surface/60 p-3">
              <UserAvatar profile={c.author} size="h-9 w-9" textSize="text-xs" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-slate-500">
                  <Link to={`/u/${c.author?.username}`} className="font-semibold text-slate-200 hover:text-white">{c.author?.display_name}</Link> · {timeAgo(c.created_at)}
                </p>
                <p className="mt-0.5 text-sm text-slate-300 break-words">{c.text}</p>
              </div>
              {c.can_delete && <button data-testid={`comment-delete-${c.id}`} onClick={() => remove(c.id)} aria-label="Delete comment" className="self-start p-1 text-slate-500 hover:text-nw-red"><Trash2 className="h-4 w-4" /></button>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
